import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * Phase 3I.4R — Command Centre chart primitives (pure SVG, no deps).
 *
 * Respects `prefers-reduced-motion`. Every chart tolerates empty data
 * with a polished empty state rather than a broken box.
 */

export const usePrefersReducedMotion = () => {
  const [rm, setRm] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setRm(mq.matches);
    const l = () => setRm(mq.matches);
    mq.addEventListener?.("change", l);
    return () => mq.removeEventListener?.("change", l);
  }, []);
  return rm;
};

/** Smoothly animates 0 → value over `duration` ms. */
export function CountUp({ value = 0, duration = 700, prefix = "", suffix = "",
                          decimals = 0, className, testId }) {
  const rm = usePrefersReducedMotion();
  const [n, setN] = useState(rm ? Number(value) || 0 : 0);
  const startRef = useRef(null);
  const targetRef = useRef(Number(value) || 0);
  useEffect(() => {
    targetRef.current = Number(value) || 0;
    if (rm) { setN(targetRef.current); return; }
    startRef.current = null;
    let raf;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const t = Math.min(1, (ts - startRef.current) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setN(targetRef.current * eased);
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, rm]);
  const display = decimals > 0 ? n.toFixed(decimals) : Math.round(n).toLocaleString();
  return <span className={className} data-testid={testId}>{prefix}{display}{suffix}</span>;
}

/** Ring gauge / percentage — 0-100. */
export function ProgressRing({ value = 0, size = 160, stroke = 14,
                                 track = "#F0EBE3", fill = "#FF6A00",
                                 label, sub, testId }) {
  const rm = usePrefersReducedMotion();
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const dash = (pct / 100) * c;
  const style = rm ? {} : { transition: "stroke-dashoffset 900ms cubic-bezier(0.22,1,0.36,1)" };
  return (
    <div className="relative inline-block" data-testid={testId} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={track} strokeWidth={stroke}/>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={fill} strokeWidth={stroke}
                strokeLinecap="round" strokeDasharray={c}
                strokeDashoffset={c - dash}
                transform={`rotate(-90 ${size/2} ${size/2})`}
                style={style}/>
      </svg>
      {label && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="text-3xl font-black tabular-nums">{label}</div>
          {sub && <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] font-bold mt-1">{sub}</div>}
        </div>
      )}
    </div>
  );
}

/** Conversion multi-ring — draws 3 nested rings for Opened/Clicked/Interested against Sent. */
export function ConversionRings({ sent = 0, opened = 0, clicked = 0, interested = 0,
                                    size = 220, testId }) {
  const rm = usePrefersReducedMotion();
  const rings = [
    { r: (size / 2) - 12, stroke: 14, color: "#FF6A00", value: opened,
      label: "Opened",     pct: sent ? (opened / sent) * 100 : 0 },
    { r: (size / 2) - 32, stroke: 12, color: "#FFA25C", value: clicked,
      label: "Clicked",    pct: sent ? (clicked / sent) * 100 : 0 },
    { r: (size / 2) - 50, stroke: 10, color: "#0F7B3E", value: interested,
      label: "Interested", pct: sent ? (interested / sent) * 100 : 0 },
  ];
  return (
    <div className="relative inline-block" data-testid={testId} style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {rings.map((ring, i) => {
          const c = 2 * Math.PI * ring.r;
          const dash = (Math.min(100, ring.pct) / 100) * c;
          const style = rm ? {} : { transition: "stroke-dashoffset 900ms cubic-bezier(0.22,1,0.36,1)", transitionDelay: `${i * 120}ms` };
          return (
            <g key={i}>
              <circle cx={size/2} cy={size/2} r={ring.r} fill="none" stroke="#F0EBE3" strokeWidth={ring.stroke}/>
              <circle cx={size/2} cy={size/2} r={ring.r} fill="none"
                      stroke={ring.color} strokeWidth={ring.stroke}
                      strokeLinecap="round" strokeDasharray={c}
                      strokeDashoffset={c - dash}
                      transform={`rotate(-90 ${size/2} ${size/2})`}
                      style={style}/>
            </g>
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
        <div className="text-4xl font-black tabular-nums"><CountUp value={sent}/></div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A8A8A] font-bold mt-1">Pitches Sent</div>
      </div>
    </div>
  );
}

export function DonutChart({ data = [], size = 200, testId }) {
  // data: [{label, value, color}]
  const total = data.reduce((s, d) => s + (d.value || 0), 0);
  const rm = usePrefersReducedMotion();
  const r = size / 2 - 12;
  const stroke = 20;
  const c = 2 * Math.PI * r;
  let offset = 0;
  if (total === 0) {
    return (
      <div className="flex items-center justify-center" style={{ width: size, height: size }} data-testid={testId}>
        <div className="text-[#8A8A8A] text-[12px] tracking-[0.16em] uppercase font-bold text-center">
          <div>No revenue yet</div>
          <div className="mt-1 text-[10px] font-normal normal-case tracking-normal">Data appears when the first payment lands.</div>
        </div>
      </div>
    );
  }
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} data-testid={testId}>
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F0EBE3" strokeWidth={stroke}/>
      {data.map((d, i) => {
        const val = d.value || 0;
        const dash = (val / total) * c;
        const style = rm ? {} : { transition: "stroke-dashoffset 900ms cubic-bezier(0.22,1,0.36,1)", transitionDelay: `${i * 100}ms` };
        const el = (
          <circle key={i} cx={size/2} cy={size/2} r={r} fill="none"
                  stroke={d.color} strokeWidth={stroke}
                  strokeDasharray={`${dash} ${c - dash}`}
                  strokeDashoffset={-offset}
                  transform={`rotate(-90 ${size/2} ${size/2})`}
                  style={style}>
            <title>{d.label}: {val}</title>
          </circle>
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

export function BarChartH({ data = [], max, showValues = true, testId, valueSuffix = "" }) {
  // data: [{label, value, color?}]
  const rm = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(rm);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 30); return () => clearTimeout(t); }, []);
  const _max = max ?? Math.max(1, ...data.map((d) => d.value || 0));
  return (
    <div className="space-y-2" data-testid={testId}>
      {data.map((d, i) => {
        const pct = _max > 0 ? Math.max(2, (d.value || 0) / _max * 100) : 0;
        const w = mounted ? `${pct}%` : "0%";
        return (
          <div key={i} className="text-[12px]" data-testid={`${testId}-row-${i}`}>
            <div className="flex justify-between font-bold">
              <span className="truncate">{d.label}</span>
              {showValues && <span className="text-[#8A8A8A] tabular-nums">{(d.value || 0).toLocaleString()}{valueSuffix}</span>}
            </div>
            <div className="mt-1 h-2 rounded-full bg-[#F0EBE3] overflow-hidden">
              <div
                className="h-full rounded-full"
                style={{ width: w, background: d.color || "#FF6A00",
                          transition: rm ? "none" : "width 800ms cubic-bezier(0.22,1,0.36,1)",
                          transitionDelay: `${i * 60}ms` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function LineArea({ series = [], keys = ["value"], colors = ["#FF6A00"],
                            height = 180, testId }) {
  // series: [{day, [key]: n}]
  const rm = usePrefersReducedMotion();
  const [mounted, setMounted] = useState(rm);
  useEffect(() => { const t = setTimeout(() => setMounted(true), 30); return () => clearTimeout(t); }, []);
  const pad = { top: 12, right: 8, bottom: 20, left: 28 };
  const W = 560, H = height;
  const iw = W - pad.left - pad.right, ih = H - pad.top - pad.bottom;
  const max = Math.max(1, ...series.flatMap((r) => keys.map((k) => r[k] || 0)));
  const points = (key) => series.length === 0 ? "" : series.map((r, i) => {
    const x = pad.left + (series.length === 1 ? iw/2 : (i / (series.length - 1)) * iw);
    const y = pad.top + ih - ((r[key] || 0) / max) * ih;
    return `${x},${y}`;
  }).join(" ");
  if (series.length === 0) {
    return (
      <div className="flex items-center justify-center text-[#8A8A8A] text-[12px] py-10" data-testid={testId}>
        No data in this window yet.
      </div>
    );
  }
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" data-testid={testId}>
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const y = pad.top + ih * f;
        return <line key={i} x1={pad.left} y1={y} x2={W - pad.right} y2={y} stroke="#F0EBE3" strokeWidth="1"/>;
      })}
      {keys.map((k, ki) => {
        const p = points(k);
        const areaPoints = series.length === 0 ? "" :
          `${pad.left},${pad.top + ih} ${p} ${W - pad.right},${pad.top + ih}`;
        const strokeDash = mounted ? "0" : "1000";
        return (
          <g key={k}>
            <polygon points={areaPoints} fill={colors[ki]} opacity="0.10"/>
            <polyline
              points={p} fill="none" stroke={colors[ki]} strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round"
              strokeDasharray={rm ? "0" : "1000"}
              strokeDashoffset={rm ? "0" : strokeDash}
              style={{ transition: rm ? "none" : "stroke-dashoffset 900ms cubic-bezier(0.22,1,0.36,1)" }}
            />
          </g>
        );
      })}
    </svg>
  );
}

export function Sparkline({ data = [], color = "#FF6A00", height = 32, testId }) {
  if (!data.length) return <div className="text-[#8A8A8A] text-[10px]" data-testid={testId}>—</div>;
  const W = 120, H = height;
  const max = Math.max(1, ...data);
  const min = Math.min(...data);
  const points = data.map((v, i) => {
    const x = (i / Math.max(1, data.length - 1)) * W;
    const y = H - ((v - min) / (max - min || 1)) * H;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" preserveAspectRatio="none" data-testid={testId}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

export function LivePulse({ label = "LIVE", testId }) {
  return (
    <span data-testid={testId} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-[#EBFBF2] border border-[#B7EBCF] text-[#0F7B3E] text-[9px] tracking-[0.28em] uppercase font-black">
      <span className="relative flex w-2 h-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0F7B3E] opacity-60"/>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0F7B3E]"/>
      </span>
      {label}
    </span>
  );
}
